import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFlushPendingQuizScore } from '@/hooks/use_flush_pending_quiz_score'
import { Profile, useStoreActions, useStoreState } from '@/stores'
import { cn, getToken, removeSubscribeToken, removeToken } from '@/utils'
import { AuthStatus } from '@/types'

type UserContextType = {
	user: Profile | null
	token: string | null
	login: (value: string, password: string) => void
	isLoggedIn: () => boolean
	logout: () => void
}

type Props = { children: ReactNode }

const UserContext = createContext<UserContextType>({} as UserContextType)

export const UserProvider = ({ children }: Props) => {
	const navigate = useNavigate()
	const location = useLocation()

	const {
		login: signin,
		loggedIn,
		loggedOut
	} = useStoreActions(action => action.auth)
	const { auth } = useStoreState(state => state.auth)

	const { fetch: getProfile } = useStoreActions(action => action.profile)
	const { data, error, unauthenticated } = useStoreState(state => state.profile)

	const messageAction = useStoreActions(action => action.message)

	const [token, setToken] = useState<string | null>(null)
	const [user, setUser] = useState<Profile | null>(null)
	const [isReady, setIsReady] = useState(false)

	useFlushPendingQuizScore(user, () => {
		messageAction.success('Quiz score saved successfully!')
	})

	const checkToken = useCallback(async () => {
		const token = await getToken()
		if (token) {
			setToken(token)
			loggedIn(token)
		} else {
			setTimeout(() => setIsReady(true), 500)
		}
	}, [loggedIn])

	useEffect(() => {
		checkToken()
	}, [checkToken])

	useEffect(() => {
		if (data.name) {
			setUser(data)
			setTimeout(() => setIsReady(true), 500)
		}
	}, [data])

	useEffect(() => {
		if (auth === AuthStatus.AUTHENTICATED) {
			getProfile()
		}
	}, [auth, getProfile])

	useEffect(() => {
		if (error) messageAction.error(error)
	}, [error, messageAction])

	const isLoggedIn = () => {
		return !!user
	}

	const login = async (value: string, password: string) => {
		const isEmail = value.includes('@')
		signin({
			[isEmail ? 'email' : 'phone']: value,
			password
		})
	}

	const logout = useCallback(() => {
		loggedOut(() => {
			setUser(null)
			removeToken()
			removeSubscribeToken()
			navigate('/login')
		})
	}, [loggedOut, navigate])

	useEffect(() => {
		if (unauthenticated) {
			setIsReady(true)
			logout()
		}
	}, [unauthenticated, logout])

	useEffect(() => {
		if (auth === AuthStatus.UNAUTHENTICATED && isReady) {
			const publicPaths = [
				'/',
				'/fun-game',
				'/quiz',
				'/quiz/scores',
				'/quiz/terms-and-conditions',
				'/login',
				'/register',
				'/register/otp',
				'/forgot-password'
			]
			const isPublicPrefix =
				location.pathname === '/' ||
				location.pathname.startsWith('/article')
			if (!publicPaths.includes(location.pathname) && !isPublicPrefix) {
				logout()
			}
		}
	}, [auth, isReady, logout, location.pathname])

	return (
		<UserContext.Provider value={{ user, token, login, isLoggedIn, logout }}>
			{isReady ? (
				<>{children}</>
			) : (
				<div className={cn('flex flex-col bg-white min-h-screen', 'min-h-dvh')}>
					<div className="flex flex-col flex-1 justify-center items-center">
						<img
							className="h-24 mb-8"
							src={`${process.env.PUBLIC_URL}/logo-gaga.png`}
							alt="GAGA"
						/>
						<img
							className="inline-block h-8"
							src={require('@/assets/icons/loader-blue.svg').default}
							alt="arirang"
						/>
					</div>
				</div>
			)}
		</UserContext.Provider>
	)
}

export const useAuth = () => useContext(UserContext)
